import Big from "big.js";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { SBar } from "../../SearchAsset/SearchFilterButton/styles";
import { calculateAssetsTotalPercentage } from "../../../../utils";

import {
  SLegendTitle,
  SCategory,
  SGridContainer,
  SGridLegendRow,
  SMessage,
  SPercentage,
} from "./styles";
import { useCurrentPortfolio } from "../../../../store";

const formatPercentage = (value: number, decimalPlaces?: number): string => {
  return value.toFixed(decimalPlaces);
};

export const PortfolioSummaryDonutChartLegend: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { selectedPortfolio } = useCurrentPortfolio();
  const assets = selectedPortfolio?.assets || {};
  const assetsLength = Object.keys(assets).length;

  const stocks = Object.keys(assets).filter(
    (el) =>
      assets[el].asset.portfolioCategory === "ACAO" ||
      assets[el].asset.portfolioCategory === "EUROPA"
  );
  const stocksPercentage = stocks
    .reduce(
      (acc, value) => acc.plus(Big(assets[value].percentage || 0)),
      Big(0)
    )
    .round(0)
    .toNumber();

  const funds = Object.keys(assets).filter(
    (el) => assets[el].asset.portfolioCategory === "FUNDO"
  );
  const fundsPercentage = funds
    .reduce(
      (acc, value) => acc.plus(Big(assets[value].percentage || 0)),
      Big(0)
    )
    .round(0)
    .toNumber();

  const fixedIncome = Object.keys(assets).filter(
    (el) => assets[el].asset.portfolioCategory === "RENDA_FIXA"
  );
  const fixedIncomePercentage = fixedIncome
    .reduce(
      (acc, value) => acc.plus(Big(assets[value].percentage || 0)),
      Big(0)
    )
    .round(0)
    .toNumber();

  const pension = Object.keys(assets).filter(
    (el) => assets[el].asset.portfolioCategory === "PREVIDENCIA"
  );
  const pensionPercentage = pension
    .reduce(
      (acc, value) => acc.plus(Big(assets[value].percentage || 0)),
      Big(0)
    )
    .round(0)
    .toNumber();

  const leftoverPercentage = Big(100)
    .minus(calculateAssetsTotalPercentage(assets))
    .round(0)
    .toNumber();

  return (
    <SGridContainer>
      <SLegendTitle>{t("portfolioEditorTitle")}</SLegendTitle>
      {assetsLength === 0 ? (
        <SMessage>{t("placeHolderMessage")}</SMessage>
      ) : (
        <>
          {funds.length > 0 && (
            <SGridLegendRow>
              <SBar
                barColor={
                  leftoverPercentage < 0
                    ? theme.colors.alertRed
                    : theme.colors.funds || "purple"
                }
              />
              <SPercentage>{formatPercentage(fundsPercentage)}%</SPercentage>
              <SCategory>{t("searchAsset.funds")}</SCategory>
            </SGridLegendRow>
          )}
          {stocks.length > 0 && (
            <SGridLegendRow>
              <SBar
                barColor={
                  leftoverPercentage < 0
                    ? theme.colors.alertRed
                    : theme.colors.stocks || "orange"
                }
              />
              <SPercentage>{formatPercentage(stocksPercentage)}%</SPercentage>
              <SCategory>{t("searchAsset.stocks")}</SCategory>
            </SGridLegendRow>
          )}
          {fixedIncome.length > 0 && (
            <SGridLegendRow>
              <SBar
                barColor={
                  leftoverPercentage < 0
                    ? theme.colors.alertRed
                    : theme.colors.fixedIncome || "pink"
                }
              />
              <SPercentage>
                {formatPercentage(fixedIncomePercentage)}%
              </SPercentage>
              <SCategory>{t("searchAsset.fixedIncome")}</SCategory>
            </SGridLegendRow>
          )}
          {pension.length > 0 && (
            <SGridLegendRow>
              <SBar
                barColor={
                  leftoverPercentage < 0
                    ? theme.colors.alertRed
                    : theme.colors.pension || "green"
                }
              />
              <SPercentage>{formatPercentage(pensionPercentage)}%</SPercentage>
              <SCategory>{t("searchAsset.pension")}</SCategory>
            </SGridLegendRow>
          )}
          {leftoverPercentage > 0 && (
            <SGridLegendRow>
              <SBar barColor={theme.colors.leftover || "gray"} />
              <SPercentage>{formatPercentage(leftoverPercentage)}%</SPercentage>
              <SCategory>{t("searchAsset.leftover")}</SCategory>
            </SGridLegendRow>
          )}
        </>
      )}
    </SGridContainer>
  );
};
